import { Injectable } from '@angular/core';
import { distinctUntilChanged, map, startWith } from 'rxjs';

import { TenantBootstrapService } from './tenant-bootstrap.service';
import { TenantConfig } from './tenant.model';
import {
  resolveRefundPolicy,
  resolveRulesPolicy,
  TenantPolicyView,
} from './tenant-policy-content';
import { TenantRefundPolicy, TenantRulesPolicy } from './tenant.model';
import { safeSupportEmail, supportMailto } from './tenant-support-email';
import { resolveAboutPage, TenantAboutView } from './tenant-about-content';

export type TenantSocialNetwork = 'instagram' | 'facebook' | 'youtube' | 'tiktok';

export interface TenantFooterSocialLink {
  network: TenantSocialNetwork;
  label: string;
  iconClass: string;
  providerClass: string;
  href: string;
  ariaLabel: string;
}

export interface TenantFooterContent {
  supportEmail?: string;
  supportMailto?: string;
  locationLabel?: string;
  socialLinks: TenantFooterSocialLink[];
}

export interface TenantPublicContent {
  isWorldLeagueTenant: boolean;
  leagueName: string;
  supportEmail: string;
  supportMailto: string;
  footer: TenantFooterContent;
  home: {
    heroTitle: string;
    heroHighlight: string;
    subtitle: string;
    joinCta: string;
    experienceTitle: string;
    descriptionTitle: string;
    descriptionText: string;
    features: Array<{ title: string; imageUrl: string }>;
    partners: TenantPublicPartner[];
  };
  about: TenantAboutView;
  rules: {
    titleTop: string;
    subtitle: string;
    policy: TenantPolicyView<TenantRulesPolicy>;
  };
  refund: {
    policy: TenantPolicyView<TenantRefundPolicy>;
  };
}

export interface TenantPublicPartner {
  id: string;
  name: string;
  websiteUrl?: string;
  imageUrl: string;
}

function compact(value: string | undefined | null): string {
  return String(value || '').trim();
}

function emailForTenant(tenant: TenantConfig | null): string {
  const configured = safeSupportEmail(tenant?.contact?.support_email);
  if (configured) return configured;
  return compact(tenant?.slug).toLowerCase() === 'world-league' ? 'info@worldleague.ca' : '';
}

function displayNameForTenant(tenant: TenantConfig | null): string {
  const configured = compact(tenant?.branding?.display_name) || compact(tenant?.name);
  if (configured) return configured;
  return compact(tenant?.slug).toLowerCase() === 'world-league' ? 'World League' : 'League Platform';
}

const WORLD_LEAGUE_FOOTER_DEFAULTS = {
  supportEmail: 'info@worldleague.ca',
  locationLabel: 'Montreal, QC',
  socialLinks: {
    instagram: 'https://instagram.com/worldleague.can',
    facebook: 'https://facebook.com/worldleagueca',
    youtube: 'https://youtube.com/@Worldleaguecan',
    tiktok: 'https://tiktok.com/@worldleagueca',
  },
} as const;

const WORLD_LEAGUE_HOMEPAGE_FEATURES = [
  { title: 'Game Tape', imageUrl: 'assets/md1.jpg' },
  { title: 'Divisions for All Skill Levels', imageUrl: 'assets/8W5A2154.jpg' },
  { title: 'Championship Rings & Celebration', imageUrl: 'assets/8W5A1003.jpg' },
  { title: 'Weekly Content & Highlights', imageUrl: 'assets/shot.jpg' },
  { title: 'Player Stats', imageUrl: 'assets/wl-about-2.jpg' },
  { title: 'Annual All-Star Game', imageUrl: 'assets/asg.jpg' },
];

const WORLD_LEAGUE_HOMEPAGE_PARTNERS: TenantPublicPartner[] = [
  { id: 'legacy-jonah-xu', name: 'Jonah Xu', imageUrl: 'assets/jonahxu.png' },
  { id: 'legacy-jon-cayanong', name: 'Jon Cayanong', imageUrl: 'assets/joncayanong.png' },
  { id: 'legacy-mmac', name: 'MMAC', imageUrl: 'assets/mmac.png' },
];

const SUPPORTED_SOCIAL_NETWORKS: ReadonlyArray<{
  network: TenantSocialNetwork;
  label: string;
  iconClass: string;
  providerClass: string;
}> = [
  { network: 'instagram', label: 'Instagram', iconClass: 'fab fa-instagram', providerClass: 'social-instagram' },
  { network: 'facebook', label: 'Facebook', iconClass: 'fab fa-facebook-f', providerClass: 'social-facebook' },
  { network: 'youtube', label: 'YouTube', iconClass: 'fab fa-youtube', providerClass: 'social-youtube' },
  { network: 'tiktok', label: 'TikTok', iconClass: 'fab fa-tiktok', providerClass: 'social-tiktok' },
];

function isAbsoluteHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && !url.username && !url.password && !value.startsWith('//');
  } catch {
    return false;
  }
}

function isSafeAssetUrl(value: string): boolean {
  return /^(https?:)?\/\//i.test(value) || value.startsWith('/') || /^(assets|\/assets)\//i.test(value);
}

function buildFooterContent(tenant: TenantConfig | null, leagueName: string): TenantFooterContent {
  const usesWorldLeagueDefaults = compact(tenant?.slug).toLowerCase() === 'world-league';
  const configuredEmail = safeSupportEmail(tenant?.contact?.support_email);
  const configuredLocation = compact(tenant?.contact?.location_label);
  const supportEmail = configuredEmail || (usesWorldLeagueDefaults ? WORLD_LEAGUE_FOOTER_DEFAULTS.supportEmail : undefined);
  const locationLabel = configuredLocation || (usesWorldLeagueDefaults ? WORLD_LEAGUE_FOOTER_DEFAULTS.locationLabel : undefined);
  const configuredSocialLinks = tenant?.contact?.social_links || {};

  const socialLinks = SUPPORTED_SOCIAL_NETWORKS.flatMap((provider): TenantFooterSocialLink[] => {
    const configuredHref = compact(configuredSocialLinks[provider.network]);
    const href = configuredHref || (usesWorldLeagueDefaults ? WORLD_LEAGUE_FOOTER_DEFAULTS.socialLinks[provider.network] : '');
    if (!isAbsoluteHttpUrl(href)) return [];

    return [{
      ...provider,
      href,
      ariaLabel: `Visit ${leagueName} on ${provider.label}`,
    }];
  });

  return {
    supportEmail,
    supportMailto: supportMailto(supportEmail) || undefined,
    locationLabel,
    socialLinks,
  };
}

function buildPublicContent(tenant: TenantConfig | null): TenantPublicContent {
  const leagueName = displayNameForTenant(tenant);
  const supportEmail = emailForTenant(tenant);
  const isWorldLeagueTenant = compact(tenant?.slug).toLowerCase() === 'world-league';
  const configuredFeatures = (tenant?.branding?.homepage_features || [])
    .slice(0, 3)
    .map((feature) => ({ title: compact(feature.title), imageUrl: compact(feature.image_url) }))
    .filter((feature) => feature.title && isSafeAssetUrl(feature.imageUrl));
  const homepageFeatures = configuredFeatures.length
    ? configuredFeatures
    : isWorldLeagueTenant ? WORLD_LEAGUE_HOMEPAGE_FEATURES : [];
  const configuredPartners = (tenant?.branding?.homepage_partners || [])
    .slice(0, 8)
    .map((partner) => ({
      id: compact(partner.id),
      name: compact(partner.name),
      websiteUrl: compact(partner.website_url),
      imageUrl: compact(partner.image_url),
      visible: partner.visible === true,
    }))
    .filter((partner) =>
      partner.visible && partner.id && partner.name && isSafeAssetUrl(partner.imageUrl) &&
      (!partner.websiteUrl || isAbsoluteHttpUrl(partner.websiteUrl))
    )
    .map(({ id, name, websiteUrl, imageUrl }) => ({
      id,
      name,
      ...(websiteUrl ? { websiteUrl } : {}),
      imageUrl,
    }));
  const homepagePartners = configuredPartners.length || tenant?.branding?.homepage_partners_configured === true
    ? configuredPartners
    : isWorldLeagueTenant ? WORLD_LEAGUE_HOMEPAGE_PARTNERS : [];
  const location = compact(tenant?.contact?.location_label);
  const locationPhrase = location ? ` in ${location}` : '';
  const managedHome = tenant?.content?.effective_home;

  return {
    isWorldLeagueTenant,
    leagueName,
    supportEmail,
    supportMailto: supportMailto(supportEmail),
    footer: buildFooterContent(tenant, leagueName),
    home: {
      heroTitle: compact(managedHome?.hero_title) || 'THE BEST LEAGUE',
      heroHighlight: compact(managedHome?.hero_highlight) || leagueName.toUpperCase(),
      subtitle: compact(managedHome?.subtitle) || `High-intensity games, tracked stats, and organized competition${locationPhrase} - built for players who love to compete.`,
      joinCta: compact(managedHome?.join_cta) || `Join ${leagueName}`,
      experienceTitle: compact(managedHome?.experience_title) || `The ${leagueName} Experience`,
      descriptionTitle: compact(managedHome?.description_title) || 'More than just basketball.',
      descriptionText: compact(managedHome?.description_text) || 'Game recordings, real stats, competitive divisions, and high-energy events - built for players who love the game.',
      features: homepageFeatures,
      partners: homepagePartners,
    },
    about: resolveAboutPage(tenant, leagueName),
    rules: {
      titleTop: leagueName,
      subtitle: 'Play fair, play hard, and respect the game. These rules help keep the league competitive, safe, and organized for everyone.',
      policy: resolveRulesPolicy(tenant, leagueName),
    },
    refund: {
      policy: resolveRefundPolicy(tenant, leagueName),
    },
  };
}

@Injectable({ providedIn: 'root' })
export class TenantPublicContentService {
  readonly content$ = this.tenantBootstrap.tenant$.pipe(
    startWith(this.tenantBootstrap.currentTenant),
    distinctUntilChanged(),
    map((tenant) => buildPublicContent(tenant))
  );

  constructor(private tenantBootstrap: TenantBootstrapService) {}

  get currentContent(): TenantPublicContent {
    return buildPublicContent(this.tenantBootstrap.currentTenant);
  }
}

export const tenantPublicContentDefaults = buildPublicContent(null);
